import "core-js/modules/es.array.reverse.js";
import "core-js/modules/web.dom-collections.iterator.js";
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import filterOutUndefined from "../../../Core/filterOutUndefined";
import flatten from "../../../Core/flatten";
import TerriaError from "../../../Core/TerriaError";
import GroupMixin from "../../../ModelMixins/GroupMixin";
import ReferenceMixin from "../../../ModelMixins/ReferenceMixin";
import CatalogIndexReferenceTraits from "../../../Traits/TraitsClasses/CatalogIndexReferenceTraits";
import CreateModel from "../../Definition/CreateModel";
import { BaseModel } from "../../Definition/Model";

/** The `CatalogIndexReference` is used to resolve items in the `catalogIndex` to actual models in terria.models.
 *
 * The `catalogIndex` is a "stripped-down" fully resolved tree of models generated using the `generateCatalogIndex` script.
 * An item in the `catalogIndex` will have `CatalogMemberReferenceTraits` with an additional `memberKnownContainerUniqueIds`.
 *
 * This means we can use the `catalogIndex` to search the entire catalog without loading models.
 *
 * When `loadReference` is called, it will attempt to load all parent models first (using `memberKnownContainerUniqueIds`)
 */
export default class CatalogIndexReference extends ReferenceMixin(CreateModel(CatalogIndexReferenceTraits)) {
  constructor() {
    super(...arguments);
    _defineProperty(this, "weakReference", true);
  }
  get type() {
    return CatalogIndexReference.type;
  }
  async forceLoadReference(_previousTarget) {
    var _this$name, _TerriaError$combine;
    if (this.uniqueId === undefined) {
      return;
    }

    // If member already exists - return it
    let member = this.terria.getModelById(BaseModel, this.uniqueId);
    if (member) {
      return member;
    }
    const errors = [];

    // No member exists, so try to load containers
    // Get full list of containers by recursively searching for parent models
    const findContainers = model => [...model.memberKnownContainerUniqueIds, ...flatten(filterOutUndefined(model.memberKnownContainerUniqueIds.map(parentId => {
      var _model$terria$catalog;
      const parent = (_model$terria$catalog = model.terria.catalogIndex) === null || _model$terria$catalog === void 0 || (_model$terria$catalog = _model$terria$catalog.models) === null || _model$terria$catalog === void 0 ? void 0 : _model$terria$catalog.get(parentId);
      if (parent) {
        return findContainers(parent);
      }
    })))];
    const containers = findContainers(this).reverse();

    // Load containers
    if (containers) {
      for (let i = 0; i < containers.length; i++) {
        const containerId = containers[i];
        let container = this.terria.getModelById(BaseModel, containerId);
        if (!container) {
          errors.push(TerriaError.from("Failed to find containerID ".concat(containerId)));
        }
        if (ReferenceMixin.isMixedInto(container)) {
          var _container$nestedTarg;
          // Recursively load references up to a depth of 5. This is so that we
          // correctly resolve the final target item when we have a chain of
          // references like: MagdaReference -> TerriaReference -> ... -> SomeItem
          // We limit to a depth of 5 to avoid looping forever.
          (await container.recursivelyLoadReference(5)).pushErrorTo(errors, "Failed to load reference ".concat(container.uniqueId));
          container = (_container$nestedTarg = container.nestedTarget) !== null && _container$nestedTarg !== void 0 ? _container$nestedTarg : container;
        }
        if (GroupMixin.isMixedInto(container)) {
          (await container.loadMembers()).pushErrorTo(errors, "Failed to load group ".concat(container.uniqueId));
        }
      }
    }

    // Does member exist now? - return it
    member = this.terria.getModelById(BaseModel, this.uniqueId);
    if (member) {
      // member.sourceReference = target.sourceReference
      return member;
    }
    const parentErrorMessage = new TerriaError({
      title: "Failed to find dataset \"".concat((_this$name = this.name) !== null && _this$name !== void 0 ? _this$name : this.uniqueId, "\""),
      message: {
        key: "core.terriaError.networkRequestMessage"
      },
      importance: 1
    });

    // No member exists - throw error
    throw (_TerriaError$combine = TerriaError.combine(errors, parentErrorMessage)) !== null && _TerriaError$combine !== void 0 ? _TerriaError$combine : parentErrorMessage;
  }
}
_defineProperty(CatalogIndexReference, "type", "catalog-index-reference");